import { getCityDisplayText } from '@/utils/cityUtils';

export interface GygLocationInput {
  name?: string | null;
  gt_category?: 'eat' | 'stay' | 'do' | null;
  category_name?: string | null;
  city?: string | Record<string, unknown> | null;
  a3_intl_address?: string | null;
  address?: string | { full?: string; short?: string } | null;
  city_identifying_names?: string[] | null;
  /** Singular form stored on Location / city documents. The array form is preferred when both exist. */
  city_identifying_name?: string | null;
  google_types?: string[] | null;
  google_places_data?: {
    types?: string[];
    addressComponents?: Array<GygAddressComponent>;
    address_components?: Array<GygAddressComponent>;
  } | null;
}

interface GygAddressComponent {
  types?: string[];
  longText?: string;
  shortText?: string;
  long_name?: string;
  short_name?: string;
}

export interface BuildGygPlaceQueryParams {
  placeName?: string | null;
  city?: string | null;
  country?: string | null;
  identifyingName?: string | null;
}

const COUNTRY_NAMES = new Set([
  'USA',
  'United States',
  'US',
  'United Kingdom',
  'UK',
  'Canada',
  'Australia',
]);

function trimString(value: string | Record<string, unknown> | null | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function parseIdentifyingName(identifyingName: string): { city?: string; country?: string } {
  const parts = identifyingName
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) {
    return {};
  }

  if (parts.length === 1) {
    return { city: parts[0] };
  }

  const last = parts[parts.length - 1];
  const city = parts[0];
  return { city, country: last };
}

function addressComponentsOf(location: GygLocationInput): GygAddressComponent[] | undefined {
  const data = location.google_places_data;
  const components = data?.addressComponents ?? data?.address_components;
  return Array.isArray(components) ? components : undefined;
}

function componentLabel(component: GygAddressComponent | undefined): string | undefined {
  return trimString(
    component?.longText ?? component?.long_name ?? component?.shortText ?? component?.short_name,
  );
}

function identifyingNameOf(location: GygLocationInput): string | undefined {
  return trimString(location.city_identifying_names?.[0] ?? location.city_identifying_name);
}

/** First comma segment of a street-first address is not the city. */
function looksLikeStreet(part: string): boolean {
  return (
    /^\d+/.test(part) ||
    /\b(Ave|Avenue|St|Street|Rd|Road|Blvd|Boulevard|Dr|Drive|Ln|Lane|Ct|Court|Pl|Place|Way|Carrer|Calle|Rue|Via)\b/i.test(
      part,
    )
  );
}

function cityFromAddressParts(parts: string[]): string | undefined {
  if (parts.length === 0 || COUNTRY_NAMES.has(parts[0])) {
    return undefined;
  }
  if (looksLikeStreet(parts[0]) && parts.length > 1 && !COUNTRY_NAMES.has(parts[1])) {
    return parts[1];
  }
  return parts[0];
}

function extractLocalityFromAddressComponents(
  location: GygLocationInput,
): string | undefined {
  const components = addressComponentsOf(location);
  if (!components) {
    return undefined;
  }

  const locality = components.find((component) => component?.types?.includes('locality'));
  return componentLabel(locality);
}

function extractCountryFromAddressComponents(
  location: GygLocationInput,
): string | undefined {
  const components = addressComponentsOf(location);
  if (!components) {
    return undefined;
  }

  const country = components.find((component) => component?.types?.includes('country'));
  return componentLabel(country);
}

function extractCityFromLocation(location: GygLocationInput): string | undefined {
  const directCity = trimString(location.city);
  if (directCity && !COUNTRY_NAMES.has(directCity)) {
    return directCity;
  }

  const fromComponents = extractLocalityFromAddressComponents(location);
  if (fromComponents) {
    return fromComponents;
  }

  const identifyingName = identifyingNameOf(location);
  if (identifyingName) {
    const { city } = parseIdentifyingName(identifyingName);
    if (city && !COUNTRY_NAMES.has(city) && !looksLikeStreet(city)) {
      return city;
    }
  }

  const cityDisplay = getCityDisplayText(location);
  if (cityDisplay) {
    const { city } = parseIdentifyingName(cityDisplay);
    if (city && !COUNTRY_NAMES.has(city)) {
      return city;
    }
    if (!COUNTRY_NAMES.has(cityDisplay)) {
      return cityDisplay;
    }
  }

  const a3 = trimString(location.a3_intl_address);
  if (a3) {
    const parts = a3.split(',').map((part) => part.trim()).filter(Boolean);
    const city = cityFromAddressParts(parts);
    if (city) {
      return city;
    }
  }

  return undefined;
}

function extractCountryFromLocation(location: GygLocationInput): string | undefined {
  const fromComponents = extractCountryFromAddressComponents(location);
  if (fromComponents) {
    return fromComponents;
  }

  const identifyingName = identifyingNameOf(location);
  if (identifyingName) {
    const { country } = parseIdentifyingName(identifyingName);
    if (country) {
      return country;
    }
  }

  const cityDisplay = getCityDisplayText(location);
  if (cityDisplay) {
    const parts = cityDisplay.split(',').map((part) => part.trim()).filter(Boolean);
    if (parts.length >= 2) {
      return parts[parts.length - 1];
    }
  }

  const a3 = trimString(location.a3_intl_address);
  if (a3) {
    const parts = a3.split(',').map((part) => part.trim()).filter(Boolean);
    if (parts.length >= 2) {
      return parts[parts.length - 1];
    }
  }

  return undefined;
}

/**
 * Builds a GYG search query from place name + city/country parts.
 */
export function buildGygPlaceSearchQuery(params: BuildGygPlaceQueryParams): string | null {
  const placeName = trimString(params.placeName);
  const city = trimString(params.city);
  const country = trimString(params.country);

  if (params.identifyingName) {
    const parsed = parseIdentifyingName(params.identifyingName);
    const resolvedCity = city ?? parsed.city;
    const resolvedCountry = country ?? parsed.country;
    if (placeName && resolvedCity && resolvedCountry) {
      return `${placeName}, ${resolvedCity}, ${resolvedCountry}`;
    }
    if (resolvedCity && resolvedCountry) {
      return `${resolvedCity}, ${resolvedCountry}`;
    }
  }

  if (placeName && city && country) {
    return `${placeName}, ${city}, ${country}`;
  }

  if (placeName && city) {
    return `${placeName} ${city}`;
  }

  if (city && country) {
    return `${city}, ${country}`;
  }

  if (city) {
    return city;
  }

  if (placeName) {
    return placeName;
  }

  return null;
}

/**
 * Builds a place-level GYG query from location document fields.
 */
export function buildGygSearchQueryFromLocation(
  location: GygLocationInput | null | undefined,
): string | null {
  if (!location) {
    return null;
  }

  return buildGygPlaceSearchQuery({
    placeName: location.name,
    city: extractCityFromLocation(location),
    country: extractCountryFromLocation(location),
    identifyingName: identifyingNameOf(location) ?? null,
  });
}

/**
 * Builds a destination-level GYG query for itinerary/list headers.
 */
export function buildGygDestinationSearchQuery(
  destinationLabel: string | null | undefined,
): string | null {
  const label = trimString(destinationLabel);
  if (!label) {
    return null;
  }

  const { city, country } = parseIdentifyingName(label);
  if (city && country) {
    return `${city}, ${country}`;
  }

  return label;
}
